import { useEffect, useRef } from 'react';
import { FinancingApi, SubmitPrequalResponse } from '@servicetitan/financing-api-client';
import {
    ApplicationDataStore,
    SubmittingUserStore,
    prepareSubmitPrequalRequest,
} from '@servicetitan/financing-application-shared';
import { FinancedAmountStore, SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useMutation } from '@tanstack/react-query';
import {
    SetupDataStore,
    PrequalificationResponseStore,
    useProviderConfig,
} from '@servicetitan/financing-workflow-shared';
import { DecisionDataStore } from '@servicetitan/financing-decision-shared';
import { ServerError } from '@servicetitan/financing-utils-shared';
import { mapFinancingPrequalStatusToDecision } from '../utils/mapFinancingPrequalStatus';

// Module-level fingerprint persistence to survive hook re-initializations
let persistedFinancingPrequalFingerprint: string | undefined;

export interface UseFinancingPrequalSubmissionOptions {
    autoSubmit?: boolean;
    enabled?: boolean;
    /** GreenSky Iovation fingerprint (if applicable) */
    fingerprint?: string;
    onSuccess?: (data: SubmitPrequalResponse) => void;
    onError?: (error: ServerError) => void;
}

export interface UseFinancingPrequalSubmissionReturn {
    submitPrequal: () => void;
    isSubmitting: boolean;
    isSuccess: boolean;
    error: ServerError | null;
    loanId: string | null;
    reset: () => void;
}

export const useFinancingPrequalSubmission = (
    options: UseFinancingPrequalSubmissionOptions = {},
): UseFinancingPrequalSubmissionReturn => {
    const {
        autoSubmit = true,
        enabled = true,
        fingerprint: passedFingerprint,
        onSuccess,
        onError,
    } = options;

    const [financingApi] = useDependencies(FinancingApi);
    const { applicationData } = ApplicationDataStore.useStore();
    const { submittingUser } = SubmittingUserStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { setDecisionData } = DecisionDataStore.useStore();
    const { setPrequalificationResponse } = PrequalificationResponseStore.useStore();
    const { hasMailingAddressStep } = useProviderConfig();

    const isGreenSky = setupData?.provider?.toLowerCase() === 'greensky';
    const fingerprint = persistedFinancingPrequalFingerprint ?? passedFingerprint;

    if (isGreenSky && passedFingerprint && !persistedFinancingPrequalFingerprint) {
        persistedFinancingPrequalFingerprint = passedFingerprint;
    }

    const hasSubmittedRef = useRef(false);

    const mutation = useMutation<SubmitPrequalResponse, ServerError>({
        mutationKey: ['financingPrequalSubmission', setupData?.customerId, setupData?.jobId],
        mutationFn: async () => {
            const request = prepareSubmitPrequalRequest({
                applicationData,
                providerName: setupData!.provider,
                tenantId: setupData!.tenantId,
                buId: setupData!.buId,
                requestedAmount: financedAmount,
                planNumbers: selectedPlan?.planNumber ? [selectedPlan.planNumber] : undefined,
                hasMailingAddressStep,
                iovationBlackbox: isGreenSky ? fingerprint : undefined,
                technicianLastName: submittingUser?.lastName,
                technicianPhone: submittingUser?.phone,
                technicianEmail: submittingUser?.email,
            });
            const response = await financingApi.loansSubmitPrequal(request);
            return response.data;
        },
        onSuccess: data => {
            hasSubmittedRef.current = true;
            setPrequalificationResponse(data);

            setDecisionData({
                loanId: data.loanId ?? null,
                externalLoanId: data.externalPrequalId ?? null,
                decision: mapFinancingPrequalStatusToDecision(data.status),
                planNumber: selectedPlan?.planNumber ?? null,
                description: selectedPlan?.description ?? null,
                planType: null,
                approvedAmount: data.approvedAmount ?? null,
                term: selectedPlan?.defaultTerms ?? null,
                rate: selectedPlan?.defaultRate ?? null,
                payment: selectedPlan?.defaultMonthlyPayment ?? null,
                maxCreditAmount: data.approvedAmount ?? null,
                declineRedirectUrl: null,
            });

            onSuccess?.(data);
        },
        onError: error => {
            hasSubmittedRef.current = false;
            onError?.(error);
        },
    });

    const submitPrequal = () => {
        if (!enabled || hasSubmittedRef.current || !selectedPlan) {
            return;
        }

        if (isGreenSky && !fingerprint) {
            return;
        }

        hasSubmittedRef.current = true;
        mutation.mutate();
    };

    const reset = () => {
        hasSubmittedRef.current = false;
        mutation.reset();
        if (isGreenSky) {
            persistedFinancingPrequalFingerprint = undefined;
        }
    };

    useEffect(() => {
        if (!enabled || !autoSubmit || hasSubmittedRef.current || !selectedPlan) {
            return;
        }

        if (isGreenSky && !fingerprint) {
            return;
        }

        const timer = setTimeout(() => {
            submitPrequal();
        }, 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, autoSubmit, selectedPlan?.planNumber, isGreenSky, fingerprint]);

    return {
        submitPrequal,
        isSubmitting: mutation.isPending,
        isSuccess: mutation.isSuccess,
        error: mutation.error ?? null,
        loanId: mutation.data?.loanId ?? null,
        reset,
    };
};
